import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const TRANSFER_TYPES = [
  { value: 'warehouse_to_warehouse', label: 'Warehouse to Warehouse' },
  { value: 'warehouse_to_branch', label: 'Warehouse to Branch' },

]

const RequestStock = () => {
  const [formData, setFormData] = useState({
    transfer_type: '',
    source_warehouse_id: '',
    source_branch_id: '',
    destination_warehouse_id: '',
    destination_branch_id: '',
    notes: ''
  })
  const [items, setItems] = useState([
    { product_id: '', quantity: '', reorder_level: '', supplier_id: '', notes: '' }
  ])
  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [products, setProducts] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingData(true)
        const [warehousesResponse, branchesResponse, productsResponse, suppliersResponse] = await Promise.all([
          apiClient.get(API_ENDPOINTS.WAREHOUSES),
          apiClient.get(API_ENDPOINTS.BRANCHES),
          apiClient.get('/api/products/products/'),
          apiClient.get(API_ENDPOINTS.SUPPLIERS)
        ])
        
        setWarehouses(warehousesResponse.data?.results || warehousesResponse.data || [])
        setBranches(branchesResponse.data?.results || branchesResponse.data || [])
        setProducts(productsResponse.data || [])
        setSuppliers(suppliersResponse.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load warehouses, branches, products, or suppliers. Please refresh the page.'
        })
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [])

  // Transform data for react-select
  const warehouseOptions = useMemo(() => {
    return warehouses.map(warehouse => ({
      value: warehouse.id,
      label: warehouse.name
    }))
  }, [warehouses])

  const branchOptions = useMemo(() => {
    return branches.map(branch => ({
      value: branch.id,
      label: `${branch.name} (${branch.warehouse_name || 'N/A'})`
    }))
  }, [branches])

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.id,
      label: `${product.name} (${product.sku})`
    }))
  }, [products])

  const supplierOptions = useMemo(() => {
    return suppliers.map(supplier => ({
      value: supplier.id,
      label: supplier.name
    }))
  }, [suppliers])

  // Get selected values for react-select
  const selectedTransferType = useMemo(() => {
    return formData.transfer_type 
      ? TRANSFER_TYPES.find(opt => opt.value === formData.transfer_type)
      : null
  }, [formData.transfer_type])

  const selectedSourceWarehouse = useMemo(() => {
    return formData.source_warehouse_id 
      ? warehouseOptions.find(opt => opt.value === parseInt(formData.source_warehouse_id))
      : null
  }, [formData.source_warehouse_id, warehouseOptions])

  const selectedSourceBranch = useMemo(() => {
    return formData.source_branch_id 
      ? branchOptions.find(opt => opt.value === parseInt(formData.source_branch_id))
      : null
  }, [formData.source_branch_id, branchOptions])

  const selectedDestinationWarehouse = useMemo(() => {
    return formData.destination_warehouse_id 
      ? warehouseOptions.find(opt => opt.value === parseInt(formData.destination_warehouse_id))
      : null
  }, [formData.destination_warehouse_id, warehouseOptions])

  const selectedDestinationBranch = useMemo(() => {
    return formData.destination_branch_id 
      ? branchOptions.find(opt => opt.value === parseInt(formData.destination_branch_id))
      : null
  }, [formData.destination_branch_id, branchOptions])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value === '' ? '' : value
    })
  }

  const handleSelectChange = (selectedOption, fieldName) => {
    const newFormData = { ...formData }
    
    // If transfer type changes, clear source/destination fields
    if (fieldName === 'transfer_type') {
      newFormData.source_warehouse_id = ''
      newFormData.source_branch_id = ''
      newFormData.destination_warehouse_id = ''
      newFormData.destination_branch_id = ''
    }
    
    newFormData[fieldName] = selectedOption ? selectedOption.value.toString() : ''
    setFormData(newFormData)
  }

  const handleItemChange = (index, field, value) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [field]: value === '' ? '' : value
    }
    setItems(newItems)
  }

  const handleItemSelectChange = (index, selectedOption, fieldName) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    }
    setItems(newItems)
  }

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: '', reorder_level: '', supplier_id: '', notes: '' }])
  }

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate transfer type
      if (!formData.transfer_type) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select a transfer type.'
        })
        setLoading(false)
        return
      }

      // Validate source and destination based on transfer type
      if (formData.transfer_type === 'warehouse_to_warehouse') {
        if (!formData.source_warehouse_id || !formData.destination_warehouse_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please select both source and destination warehouses.'
          })
          setLoading(false)
          return
        }
        if (formData.source_warehouse_id === formData.destination_warehouse_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Source and destination warehouses cannot be the same.'
          })
          setLoading(false)
          return
        }
      } else if (formData.transfer_type === 'warehouse_to_branch') {
        if (!formData.source_warehouse_id || !formData.destination_branch_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please select source warehouse and destination branch.'
          })
          setLoading(false)
          return
        }
      } else if (formData.transfer_type === 'branch_to_branch') {
        if (!formData.source_branch_id || !formData.destination_branch_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please select both source and destination branches.'
          })
          setLoading(false)
          return
        }
        if (formData.source_branch_id === formData.destination_branch_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Source and destination branches cannot be the same.'
          })
          setLoading(false)
          return
        }
      } else if (formData.transfer_type === 'branch_to_warehouse') {
        if (!formData.source_branch_id || !formData.destination_warehouse_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please select source branch and destination warehouse.'
          })
          setLoading(false)
          return
        }
      }

      // Validate items
      const validItems = items.filter(item => 
        item.product_id && item.quantity && item.reorder_level
      )

      if (validItems.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please add at least one product with quantity and reorder level.'
        })
        setLoading(false)
        return
      }

      // Build payload according to backend API
      const transferPayload = {
        transfer_type: formData.transfer_type,
        items: validItems.map(item => {
          const transferItem = {
            product_id: parseInt(item.product_id),
            quantity: parseFloat(item.quantity),
            reorder_level: parseFloat(item.reorder_level)
          }
          
          if (item.supplier_id) {
            transferItem.supplier_id = parseInt(item.supplier_id)
          }
          
          if (item.notes) {
            transferItem.notes = item.notes
          }
          
          return transferItem
        })
      }

      // Add source and destination based on transfer type
      if (formData.transfer_type === 'warehouse_to_warehouse') {
        transferPayload.source_warehouse_id = parseInt(formData.source_warehouse_id)
        transferPayload.destination_warehouse_id = parseInt(formData.destination_warehouse_id)
      } else if (formData.transfer_type === 'warehouse_to_branch') {
        transferPayload.source_warehouse_id = parseInt(formData.source_warehouse_id)
        transferPayload.destination_branch_id = parseInt(formData.destination_branch_id)
      } else if (formData.transfer_type === 'branch_to_branch') {
        transferPayload.source_branch_id = parseInt(formData.source_branch_id)
        transferPayload.destination_branch_id = parseInt(formData.destination_branch_id)
      } else if (formData.transfer_type === 'branch_to_warehouse') {
        transferPayload.source_branch_id = parseInt(formData.source_branch_id)
        transferPayload.destination_warehouse_id = parseInt(formData.destination_warehouse_id)
      }

      if (formData.notes) {
        transferPayload.notes = formData.notes
      }

      // Call single-transfer endpoint (supports multi-product items internally)
      const response = await apiClient.post('/api/stock/transfers/', transferPayload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: response.data?.reference_number
          ? `Stock transfer ${response.data.reference_number} created successfully!`
          : 'Stock transfer created successfully!',
        confirmButtonText: 'OK'
      })

      // Reset form
      setFormData({
        transfer_type: '',
        source_warehouse_id: '',
        source_branch_id: '',
        destination_warehouse_id: '',
        destination_branch_id: '',
        notes: ''
      })
      setItems([{ product_id: '', quantity: '', reorder_level: '', supplier_id: '', notes: '' }])
    } catch (error) {
      console.error('Error creating stock transfer:', error)
      
      let errorMessage = 'Failed to create stock transfer. Please try again.'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else {
          // Get first field error
          const firstError = Object.values(errorData)[0]
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
        }
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Determine which source/destination fields to show based on transfer type
  const showSourceWarehouse = formData.transfer_type === 'warehouse_to_warehouse' || formData.transfer_type === 'warehouse_to_branch'
  const showSourceBranch = formData.transfer_type === 'branch_to_branch' || formData.transfer_type === 'branch_to_warehouse'
  const showDestinationWarehouse = formData.transfer_type === 'warehouse_to_warehouse' || formData.transfer_type === 'branch_to_warehouse'
  const showDestinationBranch = formData.transfer_type === 'warehouse_to_branch' || formData.transfer_type === 'branch_to_branch'

  return (
    <>
      <div className="pagetitle">
        <h1>Request Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Request Stocks</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-arrow-left-right me-2"></i>
                  Request Stocks
                </h5>

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="transfer_type" className="col-sm-2 col-form-label">
                      Transfer Type <span className="text-danger">*</span>
                    </label>
                    <div className="col-sm-10">
                      <Select
                        id="transfer_type"
                        name="transfer_type"
                        value={selectedTransferType}
                        onChange={(option) => handleSelectChange(option, 'transfer_type')}
                        options={TRANSFER_TYPES}
                        isClearable
                        isSearchable
                        placeholder="Select Transfer Type"
                        isDisabled={loading || fetchingData}
                        required
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  {showSourceWarehouse && (
                    <div className="row mb-3">
                      <label htmlFor="source_warehouse_id" className="col-sm-2 col-form-label">
                        Source Warehouse <span className="text-danger">*</span>
                      </label>
                      <div className="col-sm-10">
                        <Select
                          id="source_warehouse_id"
                          name="source_warehouse_id"
                          value={selectedSourceWarehouse}
                          onChange={(option) => handleSelectChange(option, 'source_warehouse_id')}
                          options={warehouseOptions}
                          isClearable
                          isSearchable
                          placeholder="Select Source Warehouse"
                          isDisabled={loading || fetchingData}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {showSourceBranch && (
                    <div className="row mb-3">
                      <label htmlFor="source_branch_id" className="col-sm-2 col-form-label">
                        Source Branch <span className="text-danger">*</span>
                      </label>
                      <div className="col-sm-10">
                        <Select
                          id="source_branch_id"
                          name="source_branch_id"
                          value={selectedSourceBranch}
                          onChange={(option) => handleSelectChange(option, 'source_branch_id')}
                          options={branchOptions}
                          isClearable
                          isSearchable
                          placeholder="Select Source Branch"
                          isDisabled={loading || fetchingData}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {showDestinationWarehouse && (
                    <div className="row mb-3">
                      <label htmlFor="destination_warehouse_id" className="col-sm-2 col-form-label">
                        Destination Warehouse <span className="text-danger">*</span>
                      </label>
                      <div className="col-sm-10">
                        <Select
                          id="destination_warehouse_id"
                          name="destination_warehouse_id"
                          value={selectedDestinationWarehouse}
                          onChange={(option) => handleSelectChange(option, 'destination_warehouse_id')}
                          options={warehouseOptions}
                          isClearable
                          isSearchable
                          placeholder="Select Destination Warehouse"
                          isDisabled={loading || fetchingData}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {showDestinationBranch && (
                    <div className="row mb-3">
                      <label htmlFor="destination_branch_id" className="col-sm-2 col-form-label">
                        Destination Branch <span className="text-danger">*</span>
                      </label>
                      <div className="col-sm-10">
                        <Select
                          id="destination_branch_id"
                          name="destination_branch_id"
                          value={selectedDestinationBranch}
                          onChange={(option) => handleSelectChange(option, 'destination_branch_id')}
                          options={branchOptions}
                          isClearable
                          isSearchable
                          placeholder="Select Destination Branch"
                          isDisabled={loading || fetchingData}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Multi-Product Transfer */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Products</label>
                    <div className="col-sm-10">
                      {items.map((item, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="card-title mb-0">Product {index + 1}</h6>
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeItem(index)}
                                  disabled={loading || fetchingData}
                                >
                                  <i className="bi bi-trash"></i> Remove
                                </button>
                              )}
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Product <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  value={item.product_id ? productOptions.find(opt => opt.value === parseInt(item.product_id)) : null}
                                  onChange={(option) => handleItemSelectChange(index, option, 'product_id')}
                                  options={productOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Product"
                                  isDisabled={loading || fetchingData}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      borderColor: '#ced4da',
                                      '&:hover': {
                                        borderColor: '#ced4da'
                                      }
                                    })
                                  }}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                  placeholder="Enter quantity"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Reorder Level <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.reorder_level}
                                  onChange={(e) => handleItemChange(index, 'reorder_level', e.target.value)}
                                  placeholder="Enter reorder trigger level"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">Supplier</label>
                              <div className="col-sm-9">
                                <Select
                                  value={item.supplier_id ? supplierOptions.find(opt => opt.value === parseInt(item.supplier_id)) : null}
                                  onChange={(option) => handleItemSelectChange(index, option, 'supplier_id')}
                                  options={supplierOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Supplier (Optional)"
                                  isDisabled={loading || fetchingData}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      borderColor: '#ced4da',
                                      '&:hover': {
                                        borderColor: '#ced4da'
                                      }
                                    })
                                  }}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <label className="col-sm-3 col-form-label">Notes</label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={item.notes}
                                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                  placeholder="Enter notes (Optional)"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={addItem}
                        disabled={loading || fetchingData}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Another Product
                      </button>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="notes" className="col-sm-2 col-form-label">Transfer Notes</label>
                    <div className="col-sm-10">
                      <textarea
                        className="form-control"
                        id="notes"
                        name="notes"
                        rows="3"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Enter transfer notes (Optional)"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={loading || fetchingData}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating Transfer...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-left-right me-2"></i>
                            Create Stock Request
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default RequestStock
